// Sections 5, 6, 8, 13, 15 and 16: age, family and status modifiers (pregnancy, grief, stunting,
// restitution, child labour, low health, idleness, childcare, the family term of happiness,
// refusal, and the Section 15 conception factors).
import { defineContributor } from '../../core/registry';
import { mod } from '../../core/modifiers';
import { mean, piecewise } from '../../core/math';
import { memoByState } from '../../core/memo';
import {
  ageStrengthCap,
  isChild,
  isElder,
  isRetired,
  livingKin,
  needsOf,
  uncoveredUnderSix,
} from '../../core/queries';
import type { GameState, Modifier, Person, ReadContext, TargetOf } from '../../core/types';

/** Section 16 refusal: the household's workers stop working in the season after the refusal. */
function refusing(ctx: ReadContext, p: Person): boolean {
  const c = ctx.state.counters;
  if (c.lastRefusalTurn === null || c.refusingHouseholds === undefined) return false;
  const upcoming = ctx.state.currentReport === null ? ctx.state.turn + 1 : ctx.season.turn;
  return c.lastRefusalTurn === upcoming - 1 && c.refusingHouseholds.includes(p.householdId);
}

/** Sections 8 and 15: working parents of a child under 6 with no caretaker or parent at home. */
function parentsOfUncovered(ctx: ReadContext): ReadonlySet<string> {
  return memoByState(ctx.state, 'parents-of-uncovered', () => {
    const out = new Set<string>();
    for (const id of uncoveredUnderSix(ctx.cfg, ctx.state))
      for (const parent of ctx.state.people[id]?.parentIds ?? []) out.add(parent);
    return out;
  });
}

/** Section 15 food factor: total food demand this season, for the "household had surplus" test. */
function seasonFoodDemand(state: GameState): number {
  return memoByState(state, 'season-food-demand', () => {
    const demand = state.currentReport?.demand.food ?? {};
    let total = 0;
    for (const v of Object.values(demand)) total += v;
    return total;
  });
}

/** Section 16 family term: +-10 by whether kin are here and fed (D-34). */
function familyTerm(ctx: ReadContext, p: Person): Modifier {
  const h = ctx.cfg.happiness;
  const kin = livingKin(ctx.state, p);
  const share =
    kin.length === 0
      ? h.familyAloneShare
      : 0.5 + 0.5 * mean(kin.map((k) => Math.min(1, needsOf(ctx.state, k.id).food)));
  const note =
    kin.length === 0 ? 'no family here' : `family fed ${Math.round((share * 2 - 1) * 100)}%`;
  return mod.add('family:household', h.familySpan * (2 * share - 1), note);
}

/** Section 15 conception factors for one couple (the mother is `target.motherId`). */
function conception(ctx: ReadContext, target: TargetOf<'conception'>): Modifier[] {
  if (target.motherId === undefined) return [];
  const mother = ctx.state.people[target.motherId];
  if (mother === undefined) return [];
  const cfg = ctx.cfg;
  const l = cfg.lifecycle;
  const father = mother.partnerId === null ? undefined : ctx.state.people[mother.partnerId];
  const couple = father === undefined ? [mother] : [mother, father];
  const out: Modifier[] = [];
  const happiness = mean(couple.map((q) => q.happiness));
  out.push(
    mod.mul(
      'mind:happiness',
      piecewise(l.conceptionHappinessCurve, happiness),
      `happiness ${Math.round(happiness)}`,
    ),
  );
  const food = couple.map((q) => needsOf(ctx.state, q.id).food);
  if (food.some((f) => f < cfg.needs.shortThreshold)) {
    out.push(mod.mul('need:food', l.conceptionHungryFactor, 'hungry last season'));
  } else if (
    food.every((f) => f >= cfg.needs.metThreshold) &&
    ctx.state.stocks.food.amount >= l.conceptionSurplusSeasons * seasonFoodDemand(ctx.state) &&
    seasonFoodDemand(ctx.state) > 0
  ) {
    out.push(mod.mul('need:food', l.conceptionSurplusFactor, 'food to spare'));
  }
  const household = ctx.state.households[mother.householdId];
  const crowded = (ctx.state.currentReport?.crowding[mother.householdId] ?? 0) > 0;
  if (household === undefined || household.buildingId === null || crowded)
    out.push(mod.mul('need:shelter', l.conceptionOvercrowdedFactor, 'over capacity'));
  if (father !== undefined && father.householdId !== mother.householdId)
    out.push(mod.mul('family:apart', l.pairingNoBedsFertilityMul, 'no beds together'));
  const children = mother.childIds.filter((id) => ctx.state.people[id] !== undefined).length;
  if (children >= l.conceptionFamilySizeAt)
    out.push(mod.mul('family:size', l.conceptionFamilySizeFactor, `${children} children`));
  return out;
}

export const ageContributor = defineContributor({
  slot: 'age',
  id: 'age:core',
  targets: [
    'demand',
    'healthLoss',
    'strengthCap',
    'output',
    'skillGain',
    'happinessTarget',
    'conception',
  ],
  modifiers(ctx, target) {
    if (target.kind === 'conception') return conception(ctx, target);
    if (!('personId' in target)) return [];
    const p = ctx.state.people[target.personId];
    if (p === undefined) return [];
    const cfg = ctx.cfg;
    const out: Modifier[] = [];
    switch (target.kind) {
      case 'demand':
        if (target.resource === 'food' && p.status.pregnant !== null) {
          out.push(mod.set('status:pregnant', cfg.needs.foodPregnant, 'pregnant'));
        }
        if (target.resource === 'water' && p.status.sick !== null) {
          out.push(mod.mul('status:sick', cfg.needs.sickWaterMul, 'sick'));
        }
        return out;
      case 'healthLoss':
        if ((target.cause === 'hunger' || target.cause === 'thirst') && isElder(cfg, p)) {
          out.push(
            mod.mul(
              'age:elder',
              cfg.needs.elderShortHealthLossMul,
              'elders tolerate short rations worse',
            ),
          );
        }
        return out;
      case 'strengthCap': {
        const cap = ageStrengthCap(cfg, p.ageSeasons);
        if (cap < cfg.people.statMax) out.push(mod.min('age:strength', cap, 'age'));
        if (p.status.stunted)
          out.push(mod.min('status:stunted', cfg.needs.stuntedStrengthCap, 'stunted'));
        if (p.health < cfg.illness.lowHealthBelow) {
          out.push(mod.min('status:low-health', cfg.illness.lowHealthStrengthCap, 'poor health'));
        }
        return out;
      }
      case 'output':
        if (isChild(cfg, p)) out.push(mod.mul('age:child', cfg.work.childYieldMul, 'child labour'));
        if (p.status.pregnant?.seasonsRemaining === 1) {
          out.push(
            mod.mul('status:pregnant', cfg.work.pregnancyThirdSeasonOutput, 'late pregnancy'),
          );
        }
        if (p.status.grieving !== null)
          out.push(mod.mul('status:grieving', cfg.lifecycle.griefOutputMul, 'grieving'));
        if (p.status.restitution !== null) {
          out.push(mod.mul('status:restitution', cfg.justice.restitution.outputMul, 'restitution'));
        }
        if (parentsOfUncovered(ctx).has(p.id)) {
          out.push(
            mod.mul(
              'family:childcare',
              1 - cfg.jobs.caretaker.parentOutputLoss,
              'a small child has no carer',
            ),
          );
        }
        if (refusing(ctx, p)) out.push(mod.mul('society:refusal', 0, 'refusing to work'));
        return out;
      case 'skillGain':
        return p.status.stunted
          ? [mod.mul('status:stunted', cfg.needs.stuntedSkillGainMul, 'stunted')]
          : [];
      case 'happinessTarget':
        out.push(familyTerm(ctx, p));
        if (p.status.grieving !== null)
          out.push(mod.add('status:grieving', cfg.happiness.grief, 'grieving'));
        if (
          p.job === 'rest' &&
          !isChild(cfg, p) &&
          !isRetired(ctx.state, p, cfg) &&
          p.streaks.idle >= cfg.jobs.rest.idleSeasonsBeforePenalty
        ) {
          out.push(mod.add('status:idle', cfg.happiness.idle, `idle ${p.streaks.idle} seasons`));
        }
        if ((ctx.state.currentReport?.crowding[p.householdId] ?? 0) > 0) {
          out.push(mod.add('status:overcrowded', cfg.building.overcrowdedHappiness, 'overcrowded'));
        }
        if (uncoveredUnderSix(cfg, ctx.state).includes(p.id)) {
          out.push(
            mod.add(
              'family:childcare',
              cfg.jobs.caretaker.uncoveredHappiness,
              'no one to mind them',
            ),
          );
        }
        return out;
      default:
        return [];
    }
  },
});
